import { useState } from 'react';
import { Play, Image as ImageIcon, Trash2, User, Mail, Clock, Download, Loader2 } from 'lucide-react';
import { formatDateTime, formatFileSize } from '../../utils/formatters';
import { deleteVideo } from '../../hooks/useVideos';

function VideoCard({ video, eventId }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const isImage = video?.mimeType?.startsWith('image/');

  const handleDelete = async (e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete this video from ${video.guestName}?`)) {
      return;
    }

    setIsDeleting(true);
    const result = await deleteVideo(video.id, video.storagePath, eventId);

    if (!result.success) {
      alert(`Error deleting video: ${result.error}`);
      setIsDeleting(false);
    }
    // If successful, the real-time listener will update the UI automatically
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      setIsDownloading(true);
      const response = await fetch(video.videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Derive filename
      const extension = video.mimeType?.startsWith('image/') ? 'jpg' : 'mp4';
      a.download = `${video.guestName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(`Error downloading file: ${err.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePlayClick = () => {
    setShowPlayer(true);
  };

  const handleClosePlayer = () => {
    setShowPlayer(false);
  };

  // Format upload date
  const uploadedAtFormatted = video.uploadedAt?.toDate
    ? formatDateTime(video.uploadedAt.toDate())
    : 'Just now';

  return (
    <>
      <div className="video-card group cursor-pointer" onClick={handlePlayClick}>
        {/* Media Thumbnail */}
        <div className="relative aspect-video bg-neutral-grey-900 rounded-lg overflow-hidden mb-4">
          {isImage ? (
            <img
              src={video.videoUrl}
              alt={`${video.guestName}'s upload`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <video
              src={`${video.videoUrl}#t=0.1`}
              className="w-full h-full object-cover"
              preload="metadata"
              muted
            />
          )}
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
            {isDeleting ? (
              <div className="w-16 h-16 bg-black/80 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gold animate-spin" />
              </div>
            ) : (
              <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                {isImage ? (
                  <ImageIcon className="w-8 h-8 text-dark-slate" />
                ) : (
                  <Play className="w-8 h-8 text-dark-slate ml-1" fill="currentColor" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Video Info */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-dark-slate">
            <User className="w-4 h-4 text-gold" />
            <span className="font-semibold">{video.guestName}</span>
          </div>

          {video.guestEmail && (
            <div className="flex items-center gap-2 text-sm text-neutral-grey-600">
              <Mail className="w-4 h-4" />
              <span className="truncate">{video.guestEmail}</span>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-neutral-grey-500">
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span>{uploadedAtFormatted}</span>
            </div>
            {video.fileSize && (
              <span>{formatFileSize(video.fileSize)}</span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleDownload}
            disabled={isDownloading || isDeleting}
            className="p-2 bg-black/50 hover:bg-gold hover:text-dark-slate text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
            title="Download file"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || isDownloading}
            className="p-2 bg-black/50 hover:bg-crimson text-white rounded-lg transition-all disabled:opacity-50"
            title="Delete video"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Media Viewer Modal */}
      {showPlayer && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={handleClosePlayer}
        >
          <div
            className="relative max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header Actions */}
            <div className="absolute -top-14 right-0 flex items-center gap-2 bg-black/50 p-2 rounded-xl backdrop-blur-sm">
              <button
                onClick={handleDownload}
                disabled={isDownloading || isDeleting}
                className="p-2 text-white hover:text-gold transition-colors disabled:opacity-50 flex items-center gap-2"
                title="Download file"
              >
                {isDownloading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                <span className="hidden sm:inline text-sm font-medium">Download</span>
              </button>
              
              <div className="w-px h-6 bg-white/20 mx-1"></div>
              
              <button
                onClick={handleDelete}
                disabled={isDeleting || isDownloading}
                className="p-2 text-white hover:text-crimson transition-colors disabled:opacity-50 flex items-center gap-2"
                title="Delete video"
              >
                {isDeleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                <span className="hidden sm:inline text-sm font-medium">Delete</span>
              </button>
              
              <div className="w-px h-6 bg-white/20 mx-1"></div>
              
              <button
                onClick={handleClosePlayer}
                className="p-2 text-white hover:text-gold transition-colors"
                title="Close"
              >
                <span className="text-xl font-bold leading-none">✕</span>
              </button>
            </div>

            {/* Media Viewer */}
            {isImage ? (
              <img
                src={video.videoUrl}
                alt={`${video.guestName}'s upload`}
                className="w-full max-h-[75vh] object-contain rounded-lg shadow-2xl bg-black"
              />
            ) : (
              <video
                src={video.videoUrl}
                controls
                autoPlay
                className="w-full rounded-lg shadow-2xl"
              />
            )}

            {/* Video Details */}
            <div className="mt-4 text-white">
              <div className="flex items-center gap-3 mb-2">
                <User className="w-5 h-5 text-gold" />
                <span className="text-lg font-semibold">{video.guestName}</span>
              </div>
              {video.guestEmail && (
                <div className="flex items-center gap-3 text-neutral-grey-300">
                  <Mail className="w-4 h-4" />
                  <span>{video.guestEmail}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default VideoCard;
